import type { CurrentUser } from '@/lib/api/domains/auth';

/*
 * Permissions module (spec §3.2, §4.6).
 * Pure predicates over the session object. No role string is compared anywhere
 * else in the codebase. FE checks are UX gates only; the server is authority.
 */

export type Role = CurrentUser['role'];
export type Session = CurrentUser;

/*
 * Role labels for the two roles the backend fixes (BFF caller.Caller.Role,
 * domain access.Role): the single TPA billing staff role and the client role.
 * The Administrator/Viewer distinction is NOT a role — it is the
 * is_administrator privilege and the permission set (glossary "Viewer",
 * "Administrator"); the predicates below read the privilege, never a role
 * string, for that distinction.
 */
const ROLE_LABELS: Record<Role, string> = {
  tpabilling: 'TPA Billing',
  client: 'Client',
};

export function getRoleLabel(role: Role): string {
  return ROLE_LABELS[role];
}

export function hasRole(session: Session | null, role: Role): boolean {
  return session?.role === role;
}

/*
 * Editing billing entities is the TPA billing STAFF role's reach (the single
 * `tpabilling` role; a client caller never edits billing entities). The
 * Administrator/Viewer split WITHIN that role is the permission set + the
 * is_administrator privilege, not a role value (glossary) — the fine-grained
 * act gates (`canPerformAct`/`canOfferAct`) and the admin-area privilege gates
 * decide those; this is the coarse "is this a staff caller who edits at all"
 * read. The server is the authority and refuses regardless; this is the
 * courtesy gate.
 */
export function canEdit(session: Session | null): boolean {
  if (!session) return false;
  return session.role === 'tpabilling';
}

/** Capability check (spec §8 seam). Empty capabilities => false. */
export function can(session: Session | null, capability: string): boolean {
  return session?.capabilities.includes(capability) ?? false;
}

/*
 * User administration is gated on the `is_administrator` PRIVILEGE, not on a
 * role (F02 R17/R31; glossary "Administrator privilege" — "the single switch on
 * an account that permission administration is gated on. No role and no
 * permission grants it"). This is the one predicate the central admin-area
 * way-in, the add/edit/deactivate affordances and the central-list route guard
 * all read, so the courtesy half is decided in exactly one place and never by
 * comparing role strings inline (AC-58d352fb, AC-d9396c36, AC-99623425).
 *
 * It is a UX gate only: the server (task 1's gate) is the authority and refuses
 * regardless. A build that only hides is worse than one that only refuses, so
 * this withholds controls while the server does the refusing.
 */
export function canAdministerUsers(session: Session | null): boolean {
  return session?.isAdministrator ?? false;
}

/*
 * Permission administration is gated on the `is_administrator` PRIVILEGE, not
 * on a role and not on any permission or set (E01.F03b.R57 courtesy half,
 * AC-4cc34c17/AC-82f1631a; R58, AC-ed81e83a/AC-db32b6a3; glossary "Administrator
 * privilege" — "the single switch on an account … No role and no permission
 * grants it"). This is the one predicate the way IN to permission
 * administration (the nav entry and the route guard) and the create/change/
 * remove controls (define a permission, compose a set, edit a set's membership)
 * all read, so the courtesy half is decided in exactly one place and never by
 * comparing role strings inline.
 *
 * It is its OWN named concern, DISTINCT from `canAdministerUsers` (F02's switch
 * gate for USER administration) and from `canPerformAct` (R55's per-act
 * capability read): F03b's R57 names PERMISSION administration, so a later
 * change to one gate does not silently redecide the other, even though all
 * three read the same underlying `is_administrator` switch today.
 *
 * It reads the privilege ALONE — never the capability surface `can` reads: a
 * person holding every permission in the catalog, including one named to look
 * like the privilege, is still withheld, because no permission confers the
 * privilege (AC-db32b6a3; R58 framing). It is a UX gate only: the server (the
 * domain gate, F03b design §4.3) is the authority and refuses a permission-
 * administration act however it is reached (AC-9b2cb43c, the rule half). A
 * build that only hides is worse than one that only refuses.
 *
 * Fail-closed: a null session withholds.
 */
export function canAdministerPermissions(session: Session | null): boolean {
  return session?.isAdministrator ?? false;
}

/*
 * Per-act courtesy gate (E01.F03b.R55; AC-f48cd752, rollup AC-ed53bcf4).
 *
 * R55 names a permission against a SINGLE ACTION, not a page (AC-d05d3ab0,
 * Identity's half). This predicate reads whether the caller holds the
 * permission for THAT ACT — per-act, never per-page, and never keyed off a
 * role or the is_administrator privilege (contrast canAdministerUsers, the R58
 * privilege gate). The exemplar act in the R55 demonstration is changing a
 * bank account.
 *
 * It is a thin UX read of the server-sourced capability surface and decides
 * nothing on its own — it agrees with `can` for a given act. The frontend
 * holds no business rules and makes no authorisation decisions
 * (service-context): it WITHHOLDS controls a person may not use (the courtesy
 * half) while the domain gate REFUSES (the rule half, F03b design §4.2/§5.1).
 * A withheld control is a courtesy and NEVER the enforcement; the 401/403
 * interceptor in app/query-client.ts is the backstop when an act is reached
 * another way (AC-ed53bcf4).
 *
 * Fail-closed: a null session, or no permission for the act, withholds.
 */
export function canPerformAct(
  session: Session | null,
  permission: string,
): boolean {
  return can(session, permission);
}

/*
 * Per-surface courtesy gate (E01.F03b.R60; AC-93489b84, AC-6c3226b8).
 *
 * R60's whole force is that an act no permission set the person holds covers is
 * withheld "at every surface it presents rather than on a single screen"
 * (AC-6c3226b8). The only way to make that true — and keep it true as surfaces
 * are added — is to decide it in exactly ONE place: a list, a detail page and a
 * search result all read THIS predicate, so none can drift and "the screen
 * somebody remembered to change" cannot happen. `ActGate` (components/composed)
 * is its component expression, wrapped at every surface.
 *
 * It is the per-surface read of R55's per-act capability predicate
 * `canPerformAct`: a surface offers an act only when the caller holds the
 * permission for THAT act, read from the server-sourced effective-permission
 * surface (`/me` capabilities, Identity's effective permissions —
 * service-context). It names one act, never a page or a role, and is DISTINCT
 * from `canAdministerPermissions` (R57/R58's is_administrator carve-out): R60 is
 * the general at-every-surface withholding of any act no set covers, not the
 * permission-administration gate.
 *
 * It equals `canPerformAct` by construction so the surface-offer decision and
 * the per-act decision can never disagree. It is the courtesy half only — a
 * withheld surface is NEVER the enforcement; the domain gate refuses the act on
 * the request whether or not it was ever offered (AC-8b85b662), and the
 * frontend's backstop is the 401/403 interceptor in app/query-client.ts.
 *
 * Fail-closed: a null session, or no permission for the act, withholds.
 */
export function canOfferAct(session: Session | null, act: string): boolean {
  return canPerformAct(session, act);
}

/*
 * The way IN to the admin AREA (the `/admin` layout route that hosts both user-
 * and permission-administration children) is gated on the `is_administrator`
 * PRIVILEGE, not on a role. The area guard must agree with its children
 * (`canAdministerUsers`, `canAdministerPermissions`) and the admin nav, so the
 * area decision is one named concern here rather than an inline `isAdmin` role
 * check that would let a privilege-but-non-admin-role account see the nav and
 * then be bounced from the area (findings 1 + 2).
 *
 * It is its OWN named predicate — distinct from `canAdministerUsers` and
 * `canAdministerPermissions` even though all three read the same switch today —
 * so a later split of the user- vs permission-administration gates does not
 * silently redecide the area gate.
 *
 * Fail-closed: a null session withholds.
 */
export function canAdministerAdminArea(session: Session | null): boolean {
  return session?.isAdministrator ?? false;
}

/*
 * Central redirect map (spec §3.6). Child guards route via this, never by
 * comparing role strings inline.
 */
export function redirectPathForRole(role: Role): string {
  // A TPA billing staff caller and a client caller both land at the app root;
  // the _portal segment and its own gate route a portal session to the portal
  // (ADR 0009). The default keeps an unknown/degraded role off to /no-access
  // (ADR 0008 degrades an unknown role to the least-privileged known one, so
  // this default is the belt-and-braces for a value the enum does not cover).
  switch (role) {
    case 'tpabilling':
    case 'client':
      return '/';
    default:
      return '/no-access';
  }
}
